import {
  ItemResponse,
  UserResponse,
  OrderItem,
  RFZOItem,
  UserAddressResponse,
  TimeSlotResponse,
  PrescriptionResponse,
  MarketplaceResponse,
  FileResponse,
} from "./shared";

// Status enums based on backend analysis
export type CnCOrderStatus =
  | "CREATED"
  | "PREPARED"
  | "SEEN"
  | "PROCESSED"
  | "CANCELED"
  | "REJECTED"
  | "TAKEN"
  | "NOT_TAKEN"
  | "FOR_DELIVERY"
  | "IN_DELIVERY"
  | "SUSPENDED";

export type RFZOTherapyStatus =
  | "REQUESTED"
  | "PROCESSED"
  | "REJECTED"
  | "CANCELED"
  | "REQUEST_EXPIRED"
  | "PROCESSED_WITHOUT_CONTENT"
  | "REJECT_REQUESTED";

export type DeliveryType =
  | "PICK_UP_BY_FRIEND"
  | "FAST_DELIVERY"
  | "STANDARD_DELIVERY"
  | "PENSION_CARD_DELIVERY";

// Transaction-specific item type (different from OrderItem)
export interface ItemInCnCTransactionResponse {
  price: number;
  priceWithoutDiscount: number;
  unitPrice: number;
  unitPriceWithoutDiscount: number;
  quantity: number;
  itemExternalName?: string;
  gift?: boolean;
  itemResponse: ItemResponse;
}

// Main API response interfaces
export interface CnCOrderResponse {
  id: number;
  userResponse: UserResponse;
  marketplaceResponse?: MarketplaceResponse;
  totalPriceWithDelivery: number;
  items: OrderItem[];
  serviceItems?: OrderItem[];
  deliveryType?: DeliveryType;
  pensionCardNumber?: string;
  deliveryPrice: number;
  userDeliveryResponse?: UserResponse;
  userAddressResponse?: UserAddressResponse;
  personalIdType?: string;
  pesonalId?: string;
  deliveryFriendFirstName?: string;
  deliveryFriendSecondName?: string;
  deliveryFriendPhoneNumber?: string;
  deliveryFriendPersonalId?: string;
  created: string; // ISO datetime string from backend
  processed?: string;
  prepared?: string;
  modified: string;
  status: CnCOrderStatus;
  willBeReadyIn?: string;
  willBeReadyInDate?: string;
  willBeDelivered?: string;
  willBeDeliveredDate?: string;
  userPhoneNumberForOrder: string;
  firstName: string;
  secondName: string;
  noteForMarketplace?: string;
  noteForUser?: string;
  noteForDelivery?: string;
  deliveryTimeSlot?: TimeSlotResponse;
  fcmToken?: string;
  orderVerificationCode?: string;
  orderPOSId?: number;
  rfzoTherapyNumber?: number;
  userLBO?: string | null;
  /** ITAM-2716: patient JMBG; absent on legacy records and on a pre-ITAM-2732 backend. */
  userJmbg?: string | null;
  userTherapyImages?: string[];
  prescriptionsLinks: PrescriptionResponse[];
  totalPriceWithoutDiscount: number;
  totalPrice: number;
  noteForCanceledStatus?: string;
}

// CRITICAL: Separate interface for transactions (different from orders!)
export interface CnCTransactionResponse {
  id: number;
  orderNumber: number; // Key difference from CnCOrderResponse
  totalPrice: number;
  totalPriceWithDiscount: number;
  deliveryPrice: number;
  totalPriceWithDelivery: number;
  totalLoyaltyPoints: number;
  deliveryType?: DeliveryType;
  pensionCardNumber?: string;
  userDeliveryResponse?: UserResponse;
  userResponse: UserResponse;
  firstName: string;
  secondName: string;
  userPhoneNumber: string;
  userLBO?: string | null;
  /** ITAM-2716: patient JMBG; absent on legacy records and on a pre-ITAM-2732 backend. */
  userJmbg?: string | null;
  personalIdType?: string;
  pesonalId?: string;
  marketplaceResponse?: MarketplaceResponse;
  items: ItemInCnCTransactionResponse[]; // Different item type!
  serviceItems?: ItemInCnCTransactionResponse[];
  userAddressResponse?: UserAddressResponse;
  created: string; // Transaction creation time
  orderCreated?: string; // Original order creation time
  orderProcessed?: string; // When order was processed
  orderPrepared?: string; // When order was prepared
  status: CnCOrderStatus;
  noteForMarketplace?: string;
  noteForUser?: string;
  noteForCanceledStatus?: string;
  deliveryTimeSlot?: TimeSlotResponse;
  prescriptionsLinks: PrescriptionResponse[];
  // Transaction-specific fields that orders don't have
  transactionNumber?: number; // Added by frontend for compatibility
  // Suspended order fields
  suspendedOrderNumber?: number;
  suspendedInfoShown?: boolean;
}

export interface RFZOTherapyResponse {
  id: number;
  userResponse: UserResponse;
  marketplaceResponse?: MarketplaceResponse;
  totalPrice: number;
  totalParticipationPrice: number;
  items: RFZOItem[];
  created: string; // ISO datetime string from backend
  modified: string;
  userPhone: string;
  firstName: string;
  secondName: string;
  userLBO?: string | null;
  /** ITAM-2716: patient JMBG; absent on legacy records and on a pre-ITAM-2732 backend. */
  userJmbg?: string | null;
  noteForMarketplace?: string;
  noteForUser?: string;
  status: RFZOTherapyStatus;
  lastTherapyDate?: string;
  totalLoyaltyPoints: number;
  totalItemLoyaltyPoints: number;
  willBeReady?: string;
  userTherapyName?: string;
  userTherapyDescription?: string;
  userTherapyFiles?: FileResponse[];
}

// Online Consultation Types
export type OnlineConsultStatus =
  | "CREATED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "INCOMPLETE"
  | "REJECTED"
  | "CANCELED"
  | "SHUTDOWN"
  | "PURGED"
  // ITAM-2740: the backend sweep marks an unanswered pharmacist call MISSED.
  | "MISSED";

export const CONSULT_STATUS_LABELS: Record<OnlineConsultStatus, string> = {
  CREATED: "Kreiran",
  IN_PROGRESS: "U toku",
  COMPLETED: "Završen",
  INCOMPLETE: "Nepotpun",
  REJECTED: "Odbijen",
  CANCELED: "Otkazan",
  SHUTDOWN: "Ugasen",
  PURGED: "Obrisan",
  MISSED: "Propušten",
};

export type OnlineConsultType =
  | "PHARMACIST_SUPPORT"
  | "MEDIC_SUPPORT"
  | "MEDIC_APPOINTMENT";

export interface OnlineConsultDetails {
  name: string | null;
  email: string;
  licenseNumber: string | null;
  userId: number;
  images: FileResponse[];
  consultPrice: number;
  marketplace?: {
    name: string;
  };
}

export interface OnlineConsultParticipantResponse {
  userId: number;
  userEmail: string;
  joinToken: string;
  joinLink: string;
  joinName: string | null;
  joinedAt: string | null;
  leftAt: string | null;
}

export interface OnlineConsultResponse {
  id: number;
  type: OnlineConsultType;
  location: string;
  roomName: string;
  status: OnlineConsultStatus;
  consultToken: string | null;
  free: boolean;
  price: number;
  createdTime: string;
  expirationTimeUtc: string;
  scheduledForTimeUtc: string;
  startTimeUtc: string | null;
  endTimeUtc: string | null;
  bookedDuration: string;
  meetingDuration: string;
  actualDuration: string | null;
  callerInfo: OnlineConsultParticipantResponse;
  consultantInfo: OnlineConsultParticipantResponse;
  consultantDetails: OnlineConsultDetails | null;
  // Optional on purpose: only backends with ITAM-2601 return these two fields.
  // When absent the report UI stays hidden, so the page degrades cleanly
  // against an older backend.
  hasReport?: boolean;
  reportCreatedUtc?: string | null;
  // ITAM-2609/2610: full report content, null until a report is written.
  // Unused by the UI so far (the PDF is the read surface) — typed so the
  // list contract stays documented. Optional (not `string | null` required)
  // for the same reason as hasReport above: the type deliberately models
  // every backend version an environment might run, not just the newest
  // contract, so a lagging backend degrades cleanly instead of lying to
  // the compiler. Listed explicitly rather than derived from
  // OPTIONAL_REPORT_SECTIONS — drift here means a missing read-only field,
  // not the silent request-body data loss the derivation exists to prevent.
  report?: string | null;
  allergies?: string | null;
  currentTherapy?: string | null;
  contactReason?: string | null;
  note?: string | null;
  followUpPlan?: string | null;
}

export interface OnlineConsultPageResponse {
  elements: OnlineConsultResponse[];
  pageNum: number;
  totalNumOfPages: number;
  totalNumOfElements: number;
}

// ITAM-2610: the five optional report sections wrap around the required savet
// text in the generated PDF (allergies/currentTherapy/contactReason above it,
// note/followUpPlan below). Field names match backend commit 82002f32.
//
// Single source of truth: the payload type, the service's omit-blank loop, and
// the modal's field keys all derive from this array, so a section added or
// renamed in one place cannot silently drop out of the request body (the
// backend ignores unknown properties, and reports are write-once).
export const OPTIONAL_REPORT_SECTIONS = [
  "allergies",
  "currentTherapy",
  "contactReason",
  "note",
  "followUpPlan",
] as const;

export type OptionalReportSection = (typeof OPTIONAL_REPORT_SECTIONS)[number];

export type OnlineConsultReportPayload = {
  report: string;
} & Partial<Record<OptionalReportSection, string>>;

export interface OnlineConsultReportResponse {
  consultId: number;
  // ISO local date-time (UTC wall-clock, no zone marker) — NOT the
  // "dd.MM.yyyy, HH:mm:ss" display string the list endpoint returns.
  reportCreatedUtc: string;
}

export interface DeliveryPermitParams {
  cncOrderId: number;
  volunteerId: number;
}

export interface DeliveryPermitResponse {
  id: number;
  deliveryPermitType:
    | "RFZO_THERAPY_PERMIT"
    | "USER_DRIVER_PERMIT"
    | "PLATFORM_PERMIT";
  signed: boolean;
  signedPermitFileName?: string;
}
